import * as React from 'react'

import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import { UserSelect } from 'modules/automations/components/blocks/UserSelect'
import locations from 'modules/locations/store/collections/locations'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import teams from 'modules/teams/store/collections/teams'

import { useCachedData } from '../../../Providers/CachedDataProvider'
import { Recipients } from '../../utils'
import type { Recipient } from '../../utils'

interface Props {
  disabled: boolean
  field: Field
  multiField?: Field
  multi?: boolean
  recipient: Recipient
}

export const NotifiableSelect = observer(
  ({ disabled, recipient, field, multiField, multi = false }: Props) => {
    const {
      data: { legalEntities },
      loading: loadingData,
    } = useCachedData()

    const { loading } = useAsyncData(async () => {
      await permissionGroups.fetch()
    }, [])

    const notifiableTypeToCollection = {
      [Recipients.team]: teams,
      [Recipients.teamLead]: teams,
      [Recipients.location]: locations,
      [Recipients.permissionGroup]: permissionGroups,
      [Recipients.legalEntity]: legalEntities?.map((item) => ({
        attributes: {
          id: item.id,
          name: item.legalName,
        },
      })),
    }

    const translations = {
      [Recipients.teamLead]: i18n.t('automations.actions.send_notification.team_label'),
      [Recipients.team]: i18n.t('automations.actions.send_notification.team_label'),
      [Recipients.location]: i18n.t('automations.actions.send_notification.location_label'),
      [Recipients.permissionGroup]: i18n.t(
        'automations.actions.send_notification.permission_group_label'
      ),
      [Recipients.legalEntity]: i18n.t('automations.actions.send_notification.legal_entity_label'),
    }

    const collection = loading || loadingData ? [] : notifiableTypeToCollection[recipient]
    const options: SelectOption[] =
      collection?.map((item) => ({
        value: item.attributes.id,
        label: item.attributes.name,
      })) || []

    const label =
      translations[recipient] || i18n.t('automations.actions.send_notification.notifiable_id')

    if (loading || loadingData) {
      return (
        <Select
          none={i18n.t('common.select_one')}
          readonly
          field={field}
          label={label}
          options={[]}
        />
      )
    }

    switch (recipient) {
      case Recipients.team:
      case Recipients.teamLead:
      case Recipients.location:
      case Recipients.permissionGroup:
      case Recipients.legalEntity:
        return (
          <Select
            none={i18n.t('common.select_one')}
            readonly={disabled}
            field={field}
            label={label}
            options={options}
          />
        )

      case Recipients.access:
        return <UserSelect disabled={disabled} field={multi ? multiField : field} multi={multi} />

      default:
        return null
    }
  }
)
